import {
  useSimulateContract,
  useWriteContract,
  useWaitForTransactionReceipt,
  useAccount,
} from 'wagmi';
import { parseEther, encodeFunctionData } from 'viem';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import BigNumber from 'bignumber.js';
import StakingABI from 'src/api/smart-contracts/abi/Staking';
import { CONTRACT_ADDRESSES } from 'src/constants';
import { trackSCWriteEvent } from 'src/utils';
import { useTwoTimestamps } from 'src/hooks/useTwoTimestamps';
import { useEstimateGas } from 'src/hooks/useEstimateGas';
import { TransactionLink } from 'src/components/TransactionLink/TransactionLink';

export function useRequestRedeem({
  amount,
  onSuccess,
}: {
  amount: string;
  onSuccess?: (timestamp: number) => void;
}) {
  const queryClient = useQueryClient();
  const { address } = useAccount();
  const amountBN = BigNumber(amount);
  const amountEther = parseEther(amount);
  const { timestamp1, timestamp2, setTimestamp2 } = useTwoTimestamps(amount);

  const encodedData = address
    ? encodeFunctionData({
        abi: StakingABI,
        functionName: 'requestRedeem',
        args: [amountEther, address, address],
      })
    : undefined;

  const explicitGasLimit = 450_000n;

  const {
    transactionCost,
    gasLimit,
    originalGasEstimate,
    isLoadingTransactionCost,
  } = useEstimateGas({
    encodedData,
    toAddress: CONTRACT_ADDRESSES.staking,
    enabled: amountBN.gt(0),
    explicitGasLimit,
  });

  const { data: config, error: simulateError } = useSimulateContract({
    abi: StakingABI,
    address: CONTRACT_ADDRESSES.staking,
    functionName: 'requestRedeem',
    args: [amountEther, address!, address!],
    gas: gasLimit,
    query: {
      enabled: !!address && amountBN.gt(0),
    },
  });

  const {
    data: writeData,
    isPending: isWriting,
    error: _writeError,
    writeContract,
  } = useWriteContract();

  const confirmationEnabled = !!writeData;
  const writeError =
    timestamp1.current === timestamp2.current ? _writeError : undefined;

  const {
    isLoading: isConfirming,
    isSuccess,
    error: confirmError,
  } = useWaitForTransactionReceipt({
    hash: writeData,
    query: { enabled: confirmationEnabled },
  });

  useEffect(() => {
    if (isSuccess) {
      notifications.show({
        title: 'Request submitted',
        message: (
          <div>
            <div>You have requested to withdraw {amount} aprMON</div>
            {writeData && <TransactionLink hash={writeData} />}
          </div>
        ),
      });
      queryClient.refetchQueries();
      onSuccess?.(
        Number(
          BigNumber(timestamp2.current).div(1000).toFixed(BigNumber.ROUND_FLOOR)
        )
      );
      console.log('Request submitted with config:', config);
      trackSCWriteEvent('request_claim_submitted', config, originalGasEstimate);
    }
  }, [isSuccess]);

  return {
    isRequesting: isWriting || isConfirming,
    requestError:
      simulateError ||
      writeError ||
      (confirmationEnabled ? confirmError : undefined),
    transactionCost: transactionCost.div(1e18),
    isLoadingTransactionCost,
    requestRedeem: config?.request
      ? () => {
          writeContract(config.request);
          setTimestamp2();
        }
      : undefined,
  };
}
